import React from "react";
import {
  Paper,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Alert
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";

import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

import TipCounterLogo from '../../Assets/Logo/TipCounterLogo_Black.png';

interface LoginPageProps {
  location: any;
  navigate: (path: string, options?: any) => void;
}

interface LoginPageState {
  username: string;
  password: string;
  errorMsg: string;
  loading: boolean;
  showPassword: boolean;
  capsLock: boolean;
}

const ACCENT = "#c79656";
const BORDER = "#EAECEF";
const INK = "#1a1a1a";

class LoginPage extends React.Component<LoginPageProps, LoginPageState> {
  static contextType = AuthContext;
  context!: AuthContextType;

  constructor(props: any) {
    super(props);
    this.onLoginClick = this.onLoginClick.bind(this);
    this.onPasswordKeyDown = this.onPasswordKeyDown.bind(this);
    this.onPasswordKeyUp = this.onPasswordKeyUp.bind(this);
    this.onPasswordBlur = this.onPasswordBlur.bind(this);

    this.state = {
      username: "",
      password: "",
      errorMsg: "",
      loading: false,
      showPassword: false,
      capsLock: false
    };
  }

  private readCaps(e: any) {
    const on =
      typeof e.getModifierState === "function" &&
      e.getModifierState("CapsLock");
    this.setState({ capsLock: !!on });
  }
  private onPasswordKeyDown(e: any) {
    if (e.key === "Enter") this.onLoginClick();
    this.readCaps(e);
  }
  private onPasswordKeyUp(e: any) {
    this.readCaps(e);
  }
  private onPasswordBlur() {
    this.setState({ capsLock: false });
  }

  private async onLoginClick() {
    if (this.state.loading) return;
    this.setState({ errorMsg: "", loading: true });

    try {
      const result = await this.context.login(
        this.state.username.trim(),
        this.state.password
      );

      if (!result.ok) {
        this.setState({
          errorMsg: result.error ?? "Invalid username or password",
          loading: false
        });
        return;
      }
      
      this.props.navigate(
        this.props.location.state?.from?.pathname ?? "/",
        { replace: true }
      );
    } catch {
      this.setState({
        errorMsg: "Unable to login. Please try again.",
        loading: false
      });
    }
  }

  public render() {
    const disabled =
      !this.state.username || !this.state.password || this.state.loading;

    return (
      <div
        style={{
          minHeight: "100dvh",
          width: "100%",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(160deg, #ffffff 0%, #faf7f1 100%)"
        }}
      >
        {/* Subtle motion keyframes */}
        <style>
          {`
            @keyframes floatSoft {
              0%   { transform: translate(0,0) scale(1); }
              50%  { transform: translate(-12px,-8px) scale(1.03); }
              100% { transform: translate(0,0) scale(1); }
            }
            @keyframes floatSoft2 {
              0%   { transform: translate(0,0) scale(1); }
              50%  { transform: translate(10px,-10px) scale(1.02); }
              100% { transform: translate(0,0) scale(1); }
            }
            @keyframes haloPulse {
              0%   { transform: translate(-50%, -50%) scale(1); opacity: .55; }
              50%  { transform: translate(-50%, -50%) scale(1.04); opacity: .70; }
              100% { transform: translate(-50%, -50%) scale(1); opacity: .55; }
            }
            @media (max-width: 720px) {
              .stack { gap: 16px !important; padding: 24px !important; }
            }
          `}
        </style>

        {/* Decorative background — minimal, accent-only, very light */}
        {/* Large soft glows */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: "-8%",
            top: "-6%",
            width: 520,
            height: 520,
            borderRadius: "50%",
            filter: "blur(60px)",
            background:
              "radial-gradient(50% 50% at 50% 50%, rgba(199,150,86,0.22) 0%, rgba(199,150,86,0.08) 60%, rgba(199,150,86,0) 80%)",
            animation: "floatSoft 26s ease-in-out infinite",
            pointerEvents: "none"
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            right: "-6%",
            bottom: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            filter: "blur(68px)",
            background:
              "radial-gradient(50% 50% at 50% 50%, rgba(199,150,86,0.20) 0%, rgba(199,150,86,0.06) 60%, rgba(199,150,86,0) 80%)",
            animation: "floatSoft2 30s ease-in-out infinite",
            pointerEvents: "none"
          }}
        />

        {/* Gentle accent halo behind the card */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: 900,
            height: 900,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background:
              "radial-gradient(closest-side, rgba(199,150,86,0.22), rgba(199,150,86,0.08) 40%, rgba(199,150,86,0) 70%)",
            filter: "blur(26px)",
            animation: "haloPulse 18s ease-in-out infinite",
            pointerEvents: "none"
          }}
        />

        {/* Content stack (logo + card) */}
        <div
          className="stack"
          style={{
            position: "relative",
            zIndex: 1,
            width: "min(560px, 92vw)",
            display: "grid",
            gap: 20,
            alignItems: "center",
            justifyItems: "center",
            padding: 8
          }}
        >
          {/* Logo + slogans */}
          <div style={{ paddingBottom: "30px"}}>
            <img src={TipCounterLogo} height={"70px"} alt="TipCounter"/>
          </div>

          {/* Login card */}
          <Paper
            elevation={0}
            style={{
              width: "100%",
              borderRadius: 16,
              padding: 28,
              border: `1px solid ${BORDER}`,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,1) 100%)",
              boxShadow:
                "0 28px 72px rgba(199,150,86,0.20), 0 8px 22px rgba(0,0,0,0.06)"
            }}
          >
            {/* Accent bar */}
            <div
              style={{
                width: 72,
                height: 6,
                background: ACCENT,
                borderRadius: 6,
                margin: "0 auto 8px"
              }}
            />

            <Typography
              variant="h5"
              style={{
                textAlign: "center",
                fontWeight: 800,
                letterSpacing: 0.3,
                color: INK
              }}
            >
              Welcome back
            </Typography>
            <Typography
              variant="body2"
              style={{ textAlign: "center", color: "rgba(0,0,0,0.6)", marginTop: 4 }}
            >
              Sign in to continue
            </Typography>

            {/* Divider */}
            <div style={{ height: 1, background: BORDER, marginTop: 16, marginBottom: 16 }} />

            <div style={{ display: "grid", gap: 16 }}>
              <TextField
                label="Username"
                type="email"
                autoComplete="username"
                autoFocus
                value={this.state.username}
                onChange={(e) => this.setState({ username: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon style={{ color: ACCENT }} />
                    </InputAdornment>
                  )
                }}
                style={{ width: "100%" }}
              />

              <TextField
                label="Password"
                type={this.state.showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={this.state.password}
                onChange={(e) => this.setState({ password: e.target.value })}
                onKeyDown={this.onPasswordKeyDown}
                onKeyUp={this.onPasswordKeyUp}
                onBlur={this.onPasswordBlur}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon style={{ color: ACCENT }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          this.setState((s) => ({ showPassword: !s.showPassword }))
                        }
                        edge="end"
                      >
                        {this.state.showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                style={{ width: "100%" }}
              />

              {this.state.capsLock && (
                <Alert
                  severity="info"
                  variant="outlined"
                  style={{ borderColor: ACCENT, color: INK }}
                >
                  Caps Lock is on
                </Alert>
              )}

              {this.state.errorMsg && (
                <Alert
                  severity="error"
                  variant="outlined"
                  style={{ borderColor: "rgba(220,38,38,0.25)" }}
                >
                  {this.state.errorMsg}
                </Alert>
              )}

              <Button
                onClick={this.onLoginClick}
                disabled={disabled}
                startIcon={<LoginIcon />}
                variant="contained"
                style={{
                  height: 48,
                  borderRadius: 12,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  backgroundColor: ACCENT,
                  color: "#fff",
                  boxShadow: "0 14px 34px rgba(199,150,86,0.28), 0 8px 16px rgba(199,150,86,0.18)"
                }}
              >
                {this.state.loading ? "Signing in…" : "Sign in"}
              </Button>

              <Typography
                variant="body2"
                style={{ textAlign: "center", color: "rgba(0,0,0,0.60)" }}
              >
                No account yet or forgot your credentials?{" "}
                <span style={{ color: ACCENT, fontWeight: 600 }}>
                  Contact an administrator.
                </span>
              </Typography>
            </div>
          </Paper>
        </div>
      </div>
    );
  }
}

function LoginPageWrapper(props: any) {
  const location = useLocation();
  const navigate = useNavigate();
  return <LoginPage {...props} location={location} navigate={navigate} />;
}

export default LoginPageWrapper;
